import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ShoppingBag, MapPin, Cookie, Menu as MenuIcon, X, Building2 } from 'lucide-react';
import { useApp } from '../context/AppContext';
import PreorderCountdown from './PreorderCountdown';
import CartDrawer from './CartDrawer';

export default function Header() {
  const { t, lang, setLang, cartCount, menuState, setCartOpen, isCompany, company } = useApp();
  const loc = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const navLink = (to, label, Icon) => {
    const active = loc.pathname === to;
    return (
      <Link
        to={to}
        onClick={() => setOpen(false)}
        className={`flex items-center gap-1.5 text-sm font-medium transition-colors hover:text-[#4b5e34] ${active ? 'text-[#4b5e34]' : 'text-neutral-700'}`}
      >
        {Icon && <Icon size={15} strokeWidth={2} />}
        {label}
      </Link>
    );
  };

  return (
    <>
    <CartDrawer />
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-neutral-200">
      <div className="max-w-[1280px] mx-auto px-5 h-[68px] flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group">
          <span className="flex items-center justify-center w-9 h-9 rounded-full bg-[#f0ede4]">
            <Cookie size={20} className="text-[#b8863b]" />
          </span>
          <span className="font-display text-[19px] leading-tight text-[#2f3826] tracking-tight">Metio Frigo</span>
        </Link>

        <nav className="hidden md:flex items-center gap-7">
          {navLink('/', t('nav_menu'))}
          {navLink('/emplacements', t('nav_fridges'), MapPin)}
        </nav>

        <div className="flex items-center gap-3">
          {/* A company client needs to see, from any page, that the wider
              catalogue and the invoice billing are switched on — and be able
              to leave. The chip is the link to /entreprise, which carries the
              sign-out. */}
          {isCompany && (
            <Link
              to="/entreprise"
              data-testid="header-company-chip"
              title={`${t('company_signed_in')} ${company?.name || ''}`}
              className="hidden sm:flex items-center gap-1.5 max-w-[190px] text-[13px] font-medium text-[#4b5e34] bg-[#eef2e6] border border-[#d6dfc6] px-2.5 py-1 rounded-full hover:bg-[#e5ebd9] transition-colors"
            >
              <Building2 size={14} strokeWidth={2} />
              <span className="truncate">{company?.name}</span>
            </Link>
          )}
          <button
            onClick={() => setLang(lang === 'fr' ? 'en' : 'fr')}
            data-testid="lang-toggle"
            className="text-sm font-semibold text-neutral-700 hover:text-[#4b5e34] px-2 py-1 rounded transition-colors"
            aria-label="Change language"
          >
            {lang === 'fr' ? 'EN' : 'FR'}
          </button>
          <PreorderCountdown />
          {/* With an empty basket this used to open the checkout, which showed
              "your basket is empty" and looked like a dead button. Send people
              to next week's meals instead — that is what there is to do. */}
          <button
            onClick={() => (cartCount > 0 ? setCartOpen(true) : navigate('/?tab=week2'))}
            data-testid={cartCount > 0 ? 'header-basket' : 'header-order-cta'}
            className="relative flex items-center gap-2 bg-[#4b5e34] hover:bg-[#3f5230] text-white text-sm font-medium pl-3.5 pr-4 py-2 rounded-lg transition-colors"
          >
            <ShoppingBag size={16} />
            <span className="hidden sm:inline">
              {cartCount > 0
                ? t('view_basket')
                : menuState && menuState.open === false ? t('order') : t('order_next_week')}
            </span>
            <span className="sm:hidden">{cartCount > 0 ? '' : t('order_now_short')}</span>
            {cartCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-[#b8863b] text-white text-[11px] font-bold w-5 h-5 flex items-center justify-center rounded-full">{cartCount}</span>
            )}
          </button>
          <button className="md:hidden text-neutral-700" onClick={() => setOpen((o) => !o)} aria-label="Menu">
            {open ? <X size={22} /> : <MenuIcon size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-neutral-200 bg-white px-5 py-4 flex flex-col gap-4">
          {navLink('/', t('nav_menu'))}
          {navLink('/emplacements', t('nav_fridges'), MapPin)}
          {isCompany && navLink('/entreprise', company?.name || t('company_login'), Building2)}
        </div>
      )}
    </header>
    </>
  );
}
